import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { useNotification } from "@refinedev/core";

import { ChipList } from "@/components/ui/chip-list";

type Option = { label: string; value: string; type: string };
type Filter = {
  field: string;
  value: string;
  operator: string;
  collection?: string;
};

const options: Option[] = [
  { value: "{{user.city}}", label: "User City", type: "context" },
  { value: "{{user.country}}", label: "User Country", type: "context" },
  { value: "{{user.geolocation}}", label: "User Geolocation", type: "context" },
  { value: "{{utcNow}}", label: "Current Time", type: "context" },
  { value: "{{user.topGenre}}", label: "User Top Genre", type: "context" },
  {
    value: "{{user.top20Genres}}",
    label: "User Top 20 Genres",
    type: "context",
  },
  {
    value: "{{user.followedArtistIds}}",
    label: "User Followed Artists",
    type: "context",
  },
  {
    value: "{{user.followedVenueIds}}",
    label: "User Followed Venues",
    type: "context",
  },
  {
    value: "{{user.savedEventIds}}",
    label: "User Saved Events",
    type: "context",
  },
  {
    value: "{{user.savedMovieIds}}",
    label: "User Saved Movies",
    type: "context",
  },
  {
    value: "{{user.savedScreeningIds}}",
    label: "User Saved Screenings",
    type: "context",
  },
  {
    value: "{{user.followedCinemas}}",
    label: "User Followed Cinemas",
    type: "context",
  },
  {
    value: "{{user.followedPersonIds}}",
    label: "User Followed People",
    type: "context",
  },
  { value: "Today", label: "Today", type: "dateTag" },
  { value: "Tonight", label: "Tonight", type: "dateTag" },
  { value: "This week", label: "This week", type: "dateTag" },
  { value: "This month", label: "This month", type: "dateTag" },
  { value: "Next two months", label: "Next two months", type: "dateTag" },
  { value: "This weekend", label: "This weekend", type: "dateTag" },
  { value: "Next weekend", label: "Next weekend", type: "dateTag" },
];

const CONTEXT_FILTER_MAP: Record<string, Filter> = {
  "{{user.city}}": {
    field: "geodata.name",
    value: "{{user.city}}",
    operator: "eq",
  },
  "{{user.country}}": {
    field: "geodata.country",
    value: "{{user.country}}",
    operator: "eq",
  },
  "{{user.geolocation}}": {
    field: "venue.geolocation",
    value: "{{user.geolocation}}, 5 km",
    operator: "within",
  },
  "{{user.topGenre}}": {
    field: "genres.name",
    value: "{{user.topGenre}}",
    operator: "in",
  },
  "{{user.top20Genres}}": {
    field: "genres.name",
    value: "{{user.top20Genres}}",
    operator: "in",
  },
  "{{user.followedArtistIds}}": {
    field: "lineup.id",
    value: "{{user.followedArtistIds}}",
    operator: "in",
  },
  "{{user.followedVenueIds}}": {
    field: "venue.id",
    value: "{{user.followedVenueIds}}",
    operator: "in",
  },
  "{{user.savedEventIds}}": {
    field: "id",
    value: "{{user.savedEventIds}}",
    operator: "in",
  },
  "{{user.savedMovieIds}}": {
    field: "movie.id",
    value: "{{user.savedMovieIds}}",
    operator: "in",
    collection: "screenings",
  },
  "{{user.savedScreeningIds}}": {
    field: "id",
    value: "{{user.savedScreeningIds}}",
    operator: "in",
    collection: "screenings",
  },
  "{{user.followedCinemas}}": {
    field: "venue.id",
    value: "{{user.followedVenueIds}}",
    operator: "in",
    collection: "screenings",
  },
  "{{user.followedPersonIds}}": {
    field: "cast.id",
    value: "{{user.followedPersonIds}}",
    operator: "in",
    collection: "screenings",
  },
};

function makeSyntheticEvent(
  fieldKey: string,
  value: string[],
): React.ChangeEvent<HTMLInputElement> {
  return {
    target: { value, name: fieldKey },
  } as unknown as React.ChangeEvent<HTMLInputElement>;
}

export const ShelfContextField: React.FC<AutoFormFieldProps> = (props) => {
  const { field, inputProps } = props;
  const form = useFormContext();
  const value: string[] = useWatch({ name: field.key }) ?? [];
  const queryRulesFilters: Filter[] =
    useWatch({ name: "queryRules.filters" }) ?? [];
  const { open } = useNotification();

  const selectedDateTags = queryRulesFilters
    .filter((f) => f.field === "dateTag")
    .map((f) => f.value);

  const handleItemClick = (item: Option) => {
    if (item.type === "context") {
      const isSelected = value.includes(item.value);

      if (isSelected) {
        inputProps.onChange(
          makeSyntheticEvent(
            field.key,
            value.filter((v) => v !== item.value),
          ),
        );
        form.setValue(
          "queryRules.filters",
          queryRulesFilters.filter((f) => f.value !== item.value),
        );
        open?.({
          type: "success",
          message: `Associated filter removed`,
        });
        return;
      }

      inputProps.onChange(
        makeSyntheticEvent(field.key, [...value, item.value]),
      );
      const associatedFilter = CONTEXT_FILTER_MAP[item.value];
      if (associatedFilter) {
        form.setValue("queryRules.filters", [
          ...queryRulesFilters,
          associatedFilter,
        ]);
        open?.({
          type: "success",
          message: `Associated filter added: ${associatedFilter.field} ${associatedFilter.operator} ${associatedFilter.value}`,
        });
      }
    } else if (item.type === "dateTag") {
      const isSelected = selectedDateTags.includes(item.value);

      if (isSelected) {
        form.setValue(
          "queryRules.filters",
          queryRulesFilters.filter(
            (f) => !(f.field === "dateTag" && f.value === item.value),
          ),
        );
        return;
      }

      form.setValue("queryRules.filters", [
        ...queryRulesFilters,
        { field: "dateTag", operator: "eq", value: item.value },
      ]);
    }
  };

  return (
    <div className="flex flex-col space-y-2 text-foreground w-full">
      <ChipList
        items={options}
        selectedItems={[...value, ...selectedDateTags]}
        onItemClick={handleItemClick}
      />
    </div>
  );
};
